import { useCallback, useEffect, useState } from 'react';
import { Upload, CheckCircle2, XCircle, ArrowLeft, Download, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import type { Specification } from '../Spec/SpecDataList.tsx';
import type { ReleaseTask } from './ReleaseTasksPage.tsx';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

interface TestData {
  materialName: string;
  parameterName: string;
  testValue: number;
  unit: string;
}

interface ComparisonResult {
  batchNumber: string;
  materialId: number | null;
  materialName: string;
  parameterName: string;
  specMin: number | null;
  specMax: number | null;
  specUnit: string | null;
  testValue: number;
  unit: string;
  passed: boolean;
  specificationId?: string;
  reason?: string;
}

const toNumericBound = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const cleaned = value.trim().replace(/,/g, '');
    if (!cleaned) return null;
    const parsed = Number(cleaned.replace(/%/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

interface CreateReleaseTaskProps {
  specifications: Specification[];
  onBack: () => void;
  onSave: (task: ReleaseTask, results: ComparisonResult[]) => void;
}

export default function CreateReleaseTask({ specifications, onBack, onSave }: CreateReleaseTaskProps) {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const taskId = searchParams.get('taskId');
  const sourceTaskId = searchParams.get('sourceTaskId');
  const isViewMode = mode === 'view';
  const isRetestMode = mode === 'retest';
  const [step, setStep] = useState<'upload' | 'compare'>('upload');
  const [batchNumber, setBatchNumber] = useState('');
  const [testData, setTestData] = useState<TestData[]>([]);
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult[]>([]);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [loadingViewData, setLoadingViewData] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('No file chosen');
  const [uploadError, setUploadError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

  const fetchSpecificationById = useCallback(async (id: string): Promise<Specification | null> => {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    const response = await fetch(`${API_URL}/specifications/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      return null;
    }

    const spec = await response.json();
    return spec as Specification;
  }, [API_URL]);

  useEffect(() => {
    const initialBatchNumber = searchParams.get('batchNumber');
    if (initialBatchNumber) {
      setBatchNumber(initialBatchNumber);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isViewMode) {
      return;
    }

    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    const loadViewData = async () => {
      setLoadingViewData(true);
      try {
        let resolvedBatch = searchParams.get('batchNumber') || '';

        if (!resolvedBatch && taskId) {
          const taskRes = await fetch(`${API_URL}/ReleaseTasks/${encodeURIComponent(taskId)}`, {
            method: 'GET',
            headers,
          });
          if (taskRes.ok) {
            const task = await taskRes.json();
            resolvedBatch = String(task?.batchNumber || '');
          }
        }

        if (!resolvedBatch) {
          throw new Error('Batch number not found for this task.');
        }

        setBatchNumber(resolvedBatch);

        const compareRes = await fetch(`${API_URL}/ReleaseTasksCompare/batch/${encodeURIComponent(resolvedBatch)}`, {
          method: 'GET',
          headers,
        });

        if (!compareRes.ok) {
          throw new Error(`Failed to load comparison results: HTTP ${compareRes.status}`);
        }

        const compareItems = (await compareRes.json()) as Array<{
          batchNumber: string;
          materialId: number;
          materialName: string;
          parameterName: string;
          parameterResult: string;
          specificationId?: number | null;
          result: string;
        }>;

        const specCache = new Map<string, Specification | null>();
        const getSpecById = async (id?: number | null) => {
          if (!id) return null;
          const key = String(id);
          if (specCache.has(key)) return specCache.get(key) ?? null;
          const spec = await fetchSpecificationById(key);
          specCache.set(key, spec);
          return spec;
        };

        const mapped = await Promise.all(compareItems.map(async (item) => {
          const spec = await getSpecById(item.specificationId);
          const specItem = spec?.items.find(
            (s) => s.parameter.trim().toLowerCase() === String(item.parameterName || '').trim().toLowerCase()
          );

          const parsedTestValue = Number(String(item.parameterResult ?? '').replace(/,/g, ''));
          const testValue = Number.isFinite(parsedTestValue) ? parsedTestValue : 0;
          const specMin = toNumericBound(specItem?.specMin ?? null);
          const specMax = toNumericBound(specItem?.specMax ?? null);
          const specUnit = specItem?.uom ?? null;
          const passed = String(item.result || '').toLowerCase().includes('pass');

          // Recalculate failure reasons based on test value and spec ranges
          let reason = '';
          if (!passed) {
            const reasons: string[] = [];
            if (specMin === null && specMax === null) {
              reasons.push('Specification range is not configured');
            }
            if (specMin !== null && testValue < specMin) {
              reasons.push(`Below minimum (${specMin})`);
            }
            if (specMax !== null && testValue > specMax) {
              reasons.push(`Above maximum (${specMax})`);
            }
            if (specUnit && specUnit.trim().toLowerCase() !== (item.parameterResult ? 'unit' : '').trim().toLowerCase()) {
              // Note: We don't have unit from compareItem, so skip unit mismatch check
            }
            reason = reasons.join('; ') || 'Failed by saved result';
          }

          return {
            batchNumber: item.batchNumber || resolvedBatch,
            materialId: item.materialId ?? null,
            materialName: item.materialName || '-',
            parameterName: item.parameterName || '-',
            specMin,
            specMax,
            specUnit,
            testValue,
            unit: specUnit || '',
            passed,
            specificationId: item.specificationId ? String(item.specificationId) : undefined,
            reason,
          } as ComparisonResult;
        }));

        setComparisonResults(mapped);
        setTestData(mapped.map((m) => ({
          materialName: m.materialName,
          parameterName: m.parameterName,
          testValue: m.testValue,
          unit: m.unit,
        })));
        setStep('compare');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load view data';
        setUploadError(message);
      } finally {
        setLoadingViewData(false);
      }
    };

    void loadViewData();
  }, [API_URL, fetchSpecificationById, isViewMode, searchParams, taskId]);

  const fetchSpecificationsByMaterialName = async (materialNames: string[]): Promise<Specification[]> => {
    const uniqueNames = Array.from(new Set(materialNames.map((name) => name.trim()).filter(Boolean)));
    if (uniqueNames.length === 0) {
      return [];
    }

    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    const fetched = await Promise.all(
      uniqueNames.map(async (name) => {
        const params = new URLSearchParams();
        params.set('range', '[0,50]');
        params.set('perPage', '50');
        params.set('filter', JSON.stringify({ productDescription: name }));

        const response = await fetch(`${API_URL}/specifications?${params.toString()}`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          return null;
        }

        const list: Specification[] = await response.json();
        const exact = list.find((spec) => spec.productDescription.trim().toLowerCase() === name.toLowerCase());
        return exact || list[0] || null;
      })
    );

    return fetched.filter((item): item is Specification => Boolean(item));
  };

  const fetchMaterialsByName = async (materialNames: string[]) => {
    const uniqueNames = Array.from(new Set(materialNames.map((name) => name.trim()).filter(Boolean)));
    if (uniqueNames.length === 0) {
      return [] as Array<{ id: number; name: string; productId: string }>;
    }

    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

    const fetched = await Promise.all(
      uniqueNames.map(async (name) => {
        const params = new URLSearchParams();
        params.set('range', '[0,50]');
        params.set('perPage', '50');
        params.set('filter', JSON.stringify({ name }));

        const response = await fetch(`${API_URL}/materials?${params.toString()}`, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          return null;
        }

        const list = await response.json();
        const exact = list.find((material: any) => String(material.name || '').trim().toLowerCase() === name.toLowerCase());
        return exact || list[0] || null;
      })
    );

    return fetched.filter((item): item is { id: number; name: string; productId: string } => Boolean(item));
  };

  const normalizeHeader = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[_\s-]+/g, '');

  const toNumber = (value: unknown) => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      if (!normalized) {
        return null;
      }

      const parsed = Number(normalized.replace(/,/g, ''));
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  };

  const parseImportedRows = (rows: Record<string, unknown>[]) => {
    return rows
      .map((row) => {
        const normalizedRow = Object.entries(row).reduce<Record<string, unknown>>((acc, [key, value]) => {
          acc[normalizeHeader(key)] = value;
          return acc;
        }, {});

        const materialName = String(
          normalizedRow.materialname ?? normalizedRow.material ?? normalizedRow.materialcode ?? ''
        ).trim();
        const parameterName = String(
          normalizedRow.parametername ?? normalizedRow.parameter ?? normalizedRow.itemname ?? ''
        ).trim();
        const unit = String(normalizedRow.unit ?? normalizedRow.testunit ?? '').trim();
        const testValue = toNumber(normalizedRow.testvalue ?? normalizedRow.value ?? normalizedRow.result);

        if (!materialName || !parameterName || testValue === null) {
          return null;
        }

        return {
          materialName,
          parameterName,
          testValue,
          unit,
        } satisfies TestData;
      })
      .filter((item): item is TestData => Boolean(item));
  };

  const readFileRows = async (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (extension === 'csv') {
      const csvText = await file.text();
      const workbook = XLSX.read(csvText, { type: 'string' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      return XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    }

    if (extension === 'xlsx' || extension === 'xls') {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      return XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    }

    throw new Error('Unsupported file type. Please upload a CSV or Excel file.');
  };

  const runComparison = async (data: TestData[]) => {
    setLoadingComparison(true);
    try {
      const materialNames = data.map((row) => row.materialName);
      const localSpecs = specifications || [];
      const materials = await fetchMaterialsByName(materialNames);
      const remoteSpecs = await fetchSpecificationsByMaterialName(materialNames);
      const mergedSpecs = [...localSpecs, ...remoteSpecs];

      const specByMaterial = new Map<string, Specification>();
      mergedSpecs.forEach((spec) => {
        const nameKey = spec.productDescription.trim().toLowerCase();

        if (!specByMaterial.has(nameKey)) {
          specByMaterial.set(nameKey, spec);
        }
      });

      const materialByName = new Map<string, { id: number; name: string; productId: string }>();
      materials.forEach((material) => {
        const key = String(material.name || '').trim().toLowerCase();
        if (!materialByName.has(key)) {
          materialByName.set(key, material);
        }
      });

      const results: ComparisonResult[] = await Promise.all(data.map(async (test) => {
        const normalizedMaterialName = test.materialName.trim().toLowerCase();
        const material = materialByName.get(normalizedMaterialName) || null;
        const spec = specByMaterial.get(normalizedMaterialName) || null;

        if (!spec) {
          return {
            batchNumber,
            materialId: material?.id ?? null,
            materialName: test.materialName,
            parameterName: test.parameterName,
            specMin: null,
            specMax: null,
            specUnit: null,
            testValue: test.testValue,
            unit: test.unit,
            passed: false,
            reason: `No specification found for product description: ${test.materialName}`,
          };
        }

        const resolvedSpec = spec.id ? await fetchSpecificationById(spec.id) : null;
        const effectiveSpec = resolvedSpec || spec;

        const specItem = effectiveSpec.items.find(
          (item) => item.parameter.trim().toLowerCase() === test.parameterName.trim().toLowerCase()
        );

        if (!specItem) {
          return {
            batchNumber,
            materialId: material?.id ?? null,
            materialName: test.materialName,
            parameterName: test.parameterName,
            specMin: null,
            specMax: null,
            specUnit: null,
            testValue: test.testValue,
            unit: test.unit,
            specificationId: effectiveSpec.id,
            passed: false,
            reason: `Parameter ${test.parameterName} is not defined in specification ${effectiveSpec.specNumber}`,
          };
        }

        const min = toNumericBound(specItem.specMin);
        const max = toNumericBound(specItem.specMax);
        const specUnit = specItem.uom || null;
        const reasons: string[] = [];

        if (min === null && max === null) {
          reasons.push('Specification range is not configured');
        }
        if (min !== null && test.testValue < min) {
          reasons.push(`Below minimum (${min})`);
        }
        if (max !== null && test.testValue > max) {
          reasons.push(`Above maximum (${max})`);
        }
        if (specUnit && test.unit && specUnit.trim().toLowerCase() !== test.unit.trim().toLowerCase()) {
          reasons.push(`Unit mismatch (spec: ${specUnit}, test: ${test.unit})`);
        }

        return {
          batchNumber,
          materialId: material?.id ?? null,
          materialName: test.materialName,
          parameterName: test.parameterName,
          specMin: min,
          specMax: max,
          specUnit,
          testValue: test.testValue,
          unit: test.unit,
          specificationId: effectiveSpec.id,
          passed: reasons.length === 0,
          reason: reasons.join('; '),
        };
      }));

      setComparisonResults(results);
      setStep('compare');
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFileName(file.name);
      setUploadError('');

      void (async () => {
        try {
          const rows = await readFileRows(file);
          const parsedData = parseImportedRows(rows);

          if (parsedData.length === 0) {
            throw new Error('No valid rows found. Required columns: MaterialName, ParameterName, TestValue, Unit.');
          }

          setTestData(parsedData);
          await runComparison(parsedData);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to read uploaded file';
          setTestData([]);
          setComparisonResults([]);
          setStep('upload');
          setUploadError(message);
        }
      })();
    } else {
      setSelectedFileName('No file chosen');
      setUploadError('');
    }
  };

  const handleSaveTask = () => {
    const saveTask = async () => {
      const passedItems = comparisonResults.filter((r) => r.passed).length;
      const allPassed = passedItems === comparisonResults.length;

      if (!batchNumber.trim()) {
        throw new Error('Batch Number is required');
      }

      if (comparisonResults.length === 0) {
        throw new Error('No comparison results to save');
      }

      const missingMaterial = comparisonResults.find((item) => item.materialId == null);
      if (missingMaterial) {
        throw new Error(`Material not found for: ${missingMaterial.materialName}`);
      }

      const token = localStorage.getItem('token');
      const payload = {
        BatchNumber: batchNumber,
        Result: allPassed ? 'passed' : 'failed',
        Items: comparisonResults.map((result) => ({
          BatchNumber: result.batchNumber,
          MaterialId: result.materialId,
          MaterialName: result.materialName,
          ParameterName: result.parameterName,
          ParameterResult: String(result.testValue),
          SpecificationId: result.specificationId ? Number(result.specificationId) : null,
          Result: result.passed ? 'passed' : 'failed',
        })),
      };

      let taskResponse;
      if (isRetestMode && sourceTaskId) {
        taskResponse = await fetch(`${API_URL}/ReleaseTasks/${encodeURIComponent(sourceTaskId)}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            BatchNumber: batchNumber,
            Result: payload.Result,
          }),
        });
      } else {
        taskResponse = await fetch(`${API_URL}/ReleaseTasks/with-items`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });
      }

      if (!taskResponse.ok) {
        const text = await taskResponse.text();
        throw new Error(text || `HTTP ${taskResponse.status}`);
      }

      const savedTask = await taskResponse.json().catch(() => null);

      if (isRetestMode) {
        const existingCompareRes = await fetch(`${API_URL}/ReleaseTasksCompare/batch/${encodeURIComponent(batchNumber)}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!existingCompareRes.ok) {
          throw new Error(`Failed to load existing comparison rows: HTTP ${existingCompareRes.status}`);
        }

        const existingCompareItems = (await existingCompareRes.json()) as Array<{ id: number }>;
        if (existingCompareItems.length > 0) {
          const deleteRes = await fetch(`${API_URL}/ReleaseTasksCompare/batchDelete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(existingCompareItems.map((item) => item.id)),
          });

          if (!deleteRes.ok) {
            const text = await deleteRes.text();
            throw new Error(text || `Failed to clear existing comparison rows: HTTP ${deleteRes.status}`);
          }
        }

        const createCompareRes = await fetch(`${API_URL}/ReleaseTasksCompare/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload.Items),
        });

        if (!createCompareRes.ok) {
          const text = await createCompareRes.text();
          throw new Error(text || `Failed to save comparison rows: HTTP ${createCompareRes.status}`);
        }
      }

      const taskSource = savedTask?.task ?? savedTask;

      const newTask: ReleaseTask = {
        id: String(taskSource?.id ?? sourceTaskId ?? Date.now().toString()),
        batchNumber,
        status: allPassed ? 'passed' : 'failed',
        result: allPassed ? 'passed' : 'failed',
        createdBy: String(localStorage.getItem('username') || '-'),
        createdAt: taskSource?.createdAt || new Date().toISOString(),
        updatedAt: taskSource?.updatedAt || new Date().toISOString(),
        totalItems: comparisonResults.length,
        passedItems,
      };

      onSave(newTask, comparisonResults);
    };

    setSavingTask(true);
    saveTask()
      .catch((error) => {
        console.error('Save release task failed', error);
        alert(error instanceof Error ? error.message : 'Save failed');
      })
      .finally(() => setSavingTask(false));
  };

  const toExportRows = () =>
    comparisonResults.map((result) => ({
      BatchNo: result.batchNumber,
      Material: result.materialName,
      Parameter: result.parameterName,
      SpecRange:
        result.specMin !== null && result.specMax !== null
          ? `${result.specMin} - ${result.specMax}`
          : result.specMin !== null
          ? `>= ${result.specMin}`
          : result.specMax !== null
          ? `<= ${result.specMax}`
          : 'N/A',
      TestValue: result.testValue,
      TestUnit: result.unit || '-',
      SpecUnit: result.specUnit || '-',
      Result: result.passed ? 'Pass' : 'Fail',
      Reason: result.reason || '',
    }));

  const handleExportExcel = () => {
    if (comparisonResults.length === 0) {
      alert('No comparison results to export.');
      return;
    }

    const rows = toExportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ComparisonResults');

    const safeBatch = (batchNumber || 'report').replace(/[^a-zA-Z0-9_-]/g, '_');
    XLSX.writeFile(workbook, `comparison-report-${safeBatch}.xlsx`);
  };

  const handleExportPdf = () => {
    if (comparisonResults.length === 0) {
      alert('No comparison results to export.');
      return;
    }

    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const safeBatch = (batchNumber || 'report').replace(/[^a-zA-Z0-9_-]/g, '_');

      doc.setFontSize(16);
      doc.text('Comparison Results Report', 40, 40);
      doc.setFontSize(10);
      doc.text(`Batch: ${batchNumber || '-'}`, 40, 58);

      const tableRows = comparisonResults.map((result) => {
        const specRange =
          result.specMin !== null && result.specMax !== null
            ? `${result.specMin} - ${result.specMax}`
            : result.specMin !== null
            ? `>= ${result.specMin}`
            : result.specMax !== null
            ? `<= ${result.specMax}`
            : 'N/A';

        return [
          result.batchNumber,
          result.materialName,
          result.parameterName,
          specRange,
          String(result.testValue),
          result.unit || '-',
          result.specUnit || '-',
          result.passed ? 'Pass' : 'Fail',
          result.reason || '',
        ];
      });

      autoTable(doc, {
        startY: 72,
        head: [['Batch', 'Material', 'Parameter', 'Spec Range', 'Test Value', 'Test Unit', 'Spec Unit', 'Result', 'Reason']],
        body: tableRows,
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [243, 244, 246], textColor: 31 },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 95 },
          2: { cellWidth: 85 },
          3: { cellWidth: 70 },
          4: { cellWidth: 60 },
          5: { cellWidth: 58 },
          6: { cellWidth: 58 },
          7: { cellWidth: 48 },
          8: { cellWidth: 120 },
        },
      });

      doc.save(`comparison-report-${safeBatch}.pdf`);
    } catch (error) {
      console.error('Export PDF failed', error);
      alert('Export PDF failed. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {!isViewMode && (
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack} className="hover:bg-gray-50">
            <ArrowLeft className="size-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl">Create Release Task</h2>
            <p className="text-sm text-gray-500">Upload test data and review comparison results</p>
          </div>
        </div>
      )}

      {/* Step indicator */}
      {!isViewMode && (
      <div className="flex items-center justify-center gap-4 py-6">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            step === 'upload' ? 'bg-blue-500 text-white shadow-lg' : 'bg-green-500 text-white'
          }`}>
            1
          </div>
          <span className={`text-sm ${step === 'upload' ? 'text-blue-500' : 'text-gray-600'}`}>
            Upload Test Data
          </span>
        </div>
        <div className={`w-16 h-1 rounded-full transition-colors ${
          step !== 'upload' ? 'bg-blue-500' : 'bg-gray-300'
        }`}></div>
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            step === 'compare' ? 'bg-blue-500 text-white shadow-lg' : 'bg-gray-300 text-gray-600'
          }`}>
            2
          </div>
          <span className={`text-sm ${step === 'compare' ? 'text-blue-500' : 'text-gray-600'}`}>
            View Comparison Results
          </span>
        </div>
      </div>
      )}

      {/* Step 1: Upload test data */}
      {!isViewMode && step === 'upload' && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between pb-4 border-b">
            <h3 className="text-xl">Upload Test Data</h3>
            <span className="text-sm text-gray-500">Step 1/2</span>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="batchNumber" className="text-sm text-gray-700">Batch Number *</Label>
              <Input
                id="batchNumber"
                value={batchNumber}
                onChange={(e) => {
                  if (isRetestMode) {
                    return;
                  }

                  setBatchNumber(e.target.value);
                }}
                placeholder="Enter batch number, e.g. 20260215001"
                className="mt-1.5"
                readOnly={isRetestMode}
                aria-readonly={isRetestMode}
                disabled={isRetestMode}
              />
              {isRetestMode && (
                <p className="mt-1 text-xs text-gray-500">Batch number is locked in retest mode and will overwrite the same batch data on save.</p>
              )}
            </div>

            {comparisonResults.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                Comparison will search specifications by imported MaterialName and then map SpecificationItems by ParameterName.
              </div>
            )}

            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
                {uploadError}
              </div>
            )}

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center space-y-4 bg-gray-50 hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <Upload className="size-12 mx-auto text-gray-400" />
              <div>
                <p className="text-lg mb-2 text-gray-700">Upload Lab Test Data</p>
                <p className="text-sm text-gray-500 mb-4">
                  Supports CSV and Excel files up to 10MB
                </p>
                <div className="flex flex-col items-center gap-3">
                  <label htmlFor="release-task-file" className="inline-flex cursor-pointer items-center rounded-md bg-white px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 hover:bg-blue-50 transition-colors">
                    Choose file to upload
                  </label>
                  <Input
                    id="release-task-file"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="sr-only"
                  />
                  <div className="text-sm text-gray-500">{selectedFileName}</div>
                </div>
              </div>
            </div>
          </div>

          {testData.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-base text-gray-700">Uploaded Data Preview</h4>
                <span className="text-sm text-gray-500">{testData.length} test items</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Material</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Parameter</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Value</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Unit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {testData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.materialName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.parameterName}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.testValue}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.unit || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Comparison result */}
      {step === 'compare' && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between pb-4 border-b">
            <div className="flex items-center gap-4">
              {isViewMode && (
                <Button variant="outline" onClick={onBack} className="hover:bg-gray-50">
                  <ArrowLeft className="size-4 mr-2" />
                  Back
                </Button>
              )}
              <div>
                <h3 className="text-xl">Comparison Results</h3>
                {isViewMode && <p className="text-sm text-gray-500">Review the saved comparison result</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleExportExcel}>
                <Download className="size-4 mr-2" />
                Export Excel
              </Button>
              <Button variant="outline" onClick={handleExportPdf}>
                <Download className="size-4 mr-2" />
                Export PDF
              </Button>
              {comparisonResults.every(r => r.passed) ? (
                <>
                  <CheckCircle2 className="size-6 text-green-500" />
                  <span className="text-lg text-green-600">All Passed</span>
                </>
              ) : (
                <>
                  <XCircle className="size-6 text-red-500" />
                  <span className="text-lg text-red-600">Failed Items Found</span>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
              <div className="text-2xl text-gray-900 mb-1">{comparisonResults.length}</div>
              <div className="text-xs text-gray-600 uppercase tracking-wide">Total Items</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl text-green-600 mb-1">
                {comparisonResults.filter(r => r.passed).length}
              </div>
              <div className="text-xs text-green-700 uppercase tracking-wide">Passed</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div className="text-2xl text-red-600 mb-1">
                {comparisonResults.filter(r => !r.passed).length}
              </div>
              <div className="text-xs text-red-700 uppercase tracking-wide">Failed</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Batch</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Material</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Parameter</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Spec Range</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Test Value</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Test Unit</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Spec Unit</th>
                  <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {comparisonResults.map((result, index) => (
                  <tr
                    key={index}
                    className={`transition-colors ${
                      result.passed ? 'bg-green-50 hover:bg-green-100' : 'bg-red-50 hover:bg-red-100'
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 font-mono">{result.batchNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{result.materialName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{result.parameterName}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {result.specMin !== null && result.specMax !== null
                        ? `${result.specMin} - ${result.specMax}`
                        : result.specMin !== null
                        ? `≥ ${result.specMin}`
                        : result.specMax !== null
                        ? `≤ ${result.specMax}`
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(() => {
                        const isAboveMax = result.specMax !== null && result.testValue > result.specMax;
                        const isBelowMin = result.specMin !== null && result.testValue < result.specMin;
                        const isOutOfRange = isAboveMax || isBelowMin;

                        return (
                          <div className="flex items-center gap-2">
                            <span className={isOutOfRange ? 'text-red-600 font-semibold' : 'text-gray-900'}>
                              {result.testValue}
                            </span>
                            {isAboveMax && (
                              <div title="Exceeds maximum">
                                <ArrowUp className="size-4 text-red-600" />
                              </div>
                            )}
                            {isBelowMin && (
                              <div title="Below minimum">
                                <ArrowDown className="size-4 text-red-600" />
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{result.unit || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{result.specUnit || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {result.passed ? (
                          <>
                            <CheckCircle2 className="size-5 text-green-600" />
                            <span className="text-sm text-green-700">Pass</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="size-5 text-red-600" />
                            <div className="text-sm text-red-700">
                              <div>Fail</div>
                              {result.reason && (
                                <div className="text-xs text-red-600 mt-0.5">({result.reason})</div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 pt-4 border-t">
            {!isViewMode && (
              <Button onClick={() => setStep('upload')} variant="outline">
                Back
              </Button>
            )}
            {!isViewMode && (
              <Button onClick={handleSaveTask} disabled={savingTask} variant="outline" className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300">
                <Save className="size-4 mr-2" />
                {savingTask ? 'Saving...' : 'Save Release Task'}
              </Button>
            )}
          </div>
        </div>
      )}

      {(loadingComparison || loadingViewData) && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg border border-gray-200 px-6 py-4 shadow-lg text-sm text-gray-700">
            {loadingViewData ? 'Loading comparison results...' : 'Comparing imported data with specifications...'}
          </div>
        </div>
      )}
    </div>
  );
}